import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchMedia, resolveUrl } from "../lib/api";
import { BUCKET_MAP } from "../data/buckets";
import Nav from "../components/Nav";
import Footer from "../components/Footer";

export default function MemoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMedia().then((d) => setItems(d)).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  const current = useMemo(() => items.find((m) => m.id === id), [items, id]);
  const siblings = useMemo(() => {
    if (!current) return [];
    return items
      .filter((m) => m.bucket === current.bucket && !m.is_deleted)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [items, current]);
  const idxInBucket = siblings.findIndex((m) => m.id === id);
  const prev = idxInBucket > 0 ? siblings[idxInBucket - 1] : null;
  const next = idxInBucket >= 0 && idxInBucket < siblings.length - 1 ? siblings[idxInBucket + 1] : null;

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F9FC] flex items-center justify-center">
        <p className="font-body text-[#334E68]">Loading memory…</p>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="min-h-screen bg-[#F7F9FC] flex flex-col items-center justify-center gap-5 px-6 text-center">
        <p className="font-display italic text-3xl text-[#0B192C]">Couldn't find that memory.</p>
        <Link to="/" className="inline-flex items-center gap-2 rounded-full bg-[#0B192C] text-white px-5 py-2.5 text-sm font-body">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
      </div>
    );
  }

  const bucket = BUCKET_MAP[current.bucket];
  const isVid = current.media_type === "video";

  return (
    <div className="min-h-screen bg-[#F7F9FC]" data-testid="memory-detail-page">
      <Nav active="memories" onNavigate={(section) => navigate(`/#${section}`)} />
      <main className="pt-28 sm:pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <button
            onClick={() => navigate(-1)}
            data-testid="memory-back"
            className="group inline-flex items-center gap-2 text-sm font-body text-[#334E68] hover:text-[#0B192C] mb-6"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            Back to memories
          </button>

          <p className="font-body text-[10px] uppercase tracking-[0.35em] text-[#7B9ACC]">
            {bucket?.label || current.bucket}
          </p>
          {current.title && (
            <h1 className="mt-2 font-display font-black text-3xl sm:text-5xl text-[#0B192C] tracking-tight leading-[1.05]">
              {current.title}
            </h1>
          )}

          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 rounded-3xl overflow-hidden bg-[#0B192C] card-shadow"
            data-testid="memory-media"
          >
            {isVid ? (
              <video
                key={current.url}
                src={resolveUrl(current.url)}
                controls
                autoPlay
                playsInline
                preload="auto"
                className="w-full max-h-[75vh] object-contain bg-black"
              />
            ) : (
              <img
                src={resolveUrl(current.url)}
                alt={current.title || "memory"}
                className="w-full max-h-[75vh] object-contain bg-black"
                loading="eager"
              />
            )}
          </motion.div>

          {current.caption && (
            <p className="mt-6 font-body text-base sm:text-lg text-[#334E68] leading-relaxed max-w-3xl">
              {current.caption}
            </p>
          )}

          <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {prev ? (
                <Link
                  to={`/memory/${prev.id}`}
                  data-testid="memory-prev"
                  className="inline-flex items-center gap-2 rounded-full border border-[#EAF0F6] bg-white text-[#0B192C] px-4 py-2 text-sm font-body hover:bg-[#EAF0F6] transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" /> Previous
                </Link>
              ) : <span className="opacity-30 text-sm font-body px-4 py-2">— start of chapter —</span>}
              {next ? (
                <Link
                  to={`/memory/${next.id}`}
                  data-testid="memory-next"
                  className="inline-flex items-center gap-2 rounded-full border border-[#EAF0F6] bg-white text-[#0B192C] px-4 py-2 text-sm font-body hover:bg-[#EAF0F6] transition-colors"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </Link>
              ) : <span className="opacity-30 text-sm font-body px-4 py-2">— end of chapter —</span>}
            </div>
            <Link
              to="/#memories"
              data-testid="memory-all"
              className="inline-flex items-center gap-2 rounded-full bg-[#0B192C] hover:bg-[#050914] text-white text-sm font-body px-5 py-2.5 transition-colors"
            >
              All chapters <Sparkles className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
